import { useState } from 'react';
import { CalendlySchedulingModal } from 'sellDirect/components/legacy/sellerDash/CalendlySchedulingModal';
import {
  AppointmentDateTimeCard,
  IAppointmentDateTimeCardStyleProps,
} from 'sellDirect/components/legacy/sellerDash/AppointmentDateTimeCard';
import { ZonedDateTime } from '@js-joda/core';
import { ORVAData, useOfferContext } from 'sellDirect/OfferContext';
import { OdProtosWebData_ORVAState as ORVAState } from '__generated__/globalTypes';

interface IOfferReviewCardWithoutContextProps extends IOfferReviewCardProps {
  earliestAvailability: ZonedDateTime | null;
  earliestAvailabilityError?: boolean;
  hasUpcomingOR: boolean;
  hasCompletedOR: boolean;
  hadUnsuccessfulORVA: boolean;
  loading: boolean;
  offerReviewAt: ZonedDateTime | null;
  hasScheduledAssessment: boolean;
}

/**
 * This components renders ORVA appointment date time card with a change CTA.
 * For displaying other appointment date time, please use <ApointmentDateTimeCard />.
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const OfferReviewCardWithoutContext: React.FC<IOfferReviewCardWithoutContextProps> = ({
  earliestAvailability,
  earliestAvailabilityError,
  hasUpcomingOR,
  hasCompletedOR,
  hadUnsuccessfulORVA,
  loading,
  offerReviewAt,
  hasScheduledAssessment,
  onlyShowWithUpcomingOR,
  phoneNumber,
  orvaData,
  ...props
}) => {
  const [isModalOpen, toggleModalOpen] = useState<boolean>(false);

  if (onlyShowWithUpcomingOR && (!hasUpcomingOR || !offerReviewAt)) {
    return null;
  }

  const onClose = () => {
    toggleModalOpen(false);
  };

  return (
    <>
      <AppointmentDateTimeCard
        earliestAvailability={earliestAvailability}
        earliestAvailabilityError={earliestAvailabilityError}
        loading={loading}
        hasScheduledOrCompletedAppointment={hasScheduledAssessment}
        hasUpcomingAppointment={hasUpcomingOR}
        selectedDateTime={
          // We only want to show the offerReviewAt as the selected date IF it is in the future.
          // If it's in the past, the AppointmentDateTimeCard will show earliestAvailability instead
          hadUnsuccessfulORVA || offerReviewAt?.isBefore(ZonedDateTime.now()) ? null : offerReviewAt
        }
        onChange={() => toggleModalOpen(true)}
        disableOnChangeCta={hasCompletedOR}
        {...props}
      />
      <CalendlySchedulingModal
        isReschedule={hasUpcomingOR}
        isOpen={isModalOpen}
        onRequestClose={onClose}
        overridePhoneNumber={phoneNumber}
        analyticsName="cake-legacy-offer-review-card-calendly-scheduling-modal"
        aria-label="Schedule appointment"
        orvaData={orvaData}
      />
    </>
  );
};

interface IOfferReviewCardProps extends IAppointmentDateTimeCardStyleProps {
  phoneNumber?: string;
  onlyShowWithUpcomingOR?: boolean;
  orvaData: ORVAData;
}

/** TODO: Add documentation */
export const OfferReviewCard: React.FC<IOfferReviewCardProps> = ({ orvaData, ...props }) => {
  const { loading: offerLoading } = useOfferContext();

  const { offerReviewAt, orvaState, earliestCalendly, earliestAvailabilityError } = orvaData;

  return (
    <OfferReviewCardWithoutContext
      offerReviewAt={offerReviewAt}
      hadUnsuccessfulORVA={orvaState === ORVAState.ORVA_ATTEMPTED}
      hasUpcomingOR={orvaState === ORVAState.ORVA_SCHEDULED}
      hasScheduledAssessment={
        orvaState === ORVAState.ORVA_SCHEDULED || orvaState === ORVAState.ORVA_COMPLETED
      }
      loading={offerLoading}
      earliestAvailability={earliestCalendly}
      earliestAvailabilityError={earliestAvailabilityError}
      hasCompletedOR={orvaState === ORVAState.ORVA_COMPLETED}
      orvaData={orvaData}
      {...props}
    />
  );
};
